import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  ErrorMessage,
  UPDATE_EMPLOYEE,
  ADD_EMPLOYEE,
  LoadingSpinner,
  Button,
  Toggle,
  MultiselectDropdown,
  OrgTreeSelector,
  ObjectPillList,
  useCurrentCompany,
  useCompanyFeature,
  PhoneNumberList,
  phoneNumberListIsValid,
  SEND_EMPLOYEE_CONSENT_LINK,
} from '@employ-engage/common-components';
import {
  BasicTableDataRow,
  Company,
  Employee,
  nearestValidDepartmentList,
  departmentStringToFriendlyString,
  ManagementRole,
  CompanyFeaturesEnum,
  ContactNumber,
  PhoneNumberDraft,
  PhoneNumberStatus,
} from '@employ-engage/data';
import { AddManagementRoleModal } from './add-management-role-modal';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import isEmpty from 'lodash.isempty';

export interface AddEmployeeFormProps {
  setOpen: (open: boolean) => void;
  employeeSelectedForEditing?: BasicTableDataRow | null;
  employeeSelectedForEditingHasValidDepartment?: boolean;
  refetchEmployeeList: () => void;
}

/** What the API takes: the number and label, and the id of a number already saved. */
const draftsForSaving = (drafts: PhoneNumberDraft[] = []) =>
  drafts.map(({ id, number, label }) => ({
    ...(id ? { id } : {}),
    number: number.trim(),
    label,
  }));

export function AddEmployeeForm({
  setOpen,
  employeeSelectedForEditing,
  employeeSelectedForEditingHasValidDepartment,
  refetchEmployeeList,
}: AddEmployeeFormProps) {
  const [addManagementRoleModalOpen, setAddManagementRoleModalOpen] =
    useState(false);
  const { t } = useTranslation(['common']);
  const [
    employeeExistsButIsArchivedErrorMessage,
    setEmployeeExistsButIsArchivedErrorMessage,
  ] = useState<string>('');
  const {
    currentCompany: company,
    loading: isCompanyLoading,
    refetchCompany,
  } = useCurrentCompany();
  const [addEmployee] = useMutation(ADD_EMPLOYEE);
  const [updateEmployee] = useMutation(UPDATE_EMPLOYEE);
  const [sendConsentLink] = useMutation(SEND_EMPLOYEE_CONSENT_LINK);
  const [consentLinkMessage, setConsentLinkMessage] = useState('');
  const { feature: multiplePhoneNumbersFeature } = useCompanyFeature(
    CompanyFeaturesEnum.MULTIPLE_PHONE_NUMBERS
  );
  const multiplePhoneNumbers = !!multiplePhoneNumbersFeature?.isEnabled;

  const savedNumbers: ContactNumber[] =
    ((employeeSelectedForEditing as unknown as Employee)?.phoneNumbers as ContactNumber[]) ?? [];
  const statusById: Record<string, PhoneNumberStatus> = Object.fromEntries(
    savedNumbers.map((n) => [n.id, n.status])
  );
  const phoneNumberDrafts: PhoneNumberDraft[] = savedNumbers.map(({ id, number, label }) => ({
    id,
    number,
    label,
  }));

  const initialValues = employeeSelectedForEditing
    ? employeeSelectedForEditingHasValidDepartment
      ? {
          managementRoles: [],
          restoreEmployee: false,
          replaceEmployee: false,
          ...(employeeSelectedForEditing as unknown as Employee),
          organization: (employeeSelectedForEditing as unknown as Employee)
            .department as string[],
          phoneNumbers: phoneNumberDrafts,
        }
      : {
          managementRoles: [],
          restoreEmployee: false,
          replaceEmployee: false,
          ...(employeeSelectedForEditing as unknown as Employee),
          organization: nearestValidDepartmentList(
            company as Company,
            (employeeSelectedForEditing as unknown as Employee)
              .department as string[]
          )[0],
          phoneNumbers: phoneNumberDrafts,
        }
    : {
        firstName: '',
        lastName: '',
        employeeId: '',
        emailAddress: '',
        managementRoles: [],
        restoreEmployee: false,
        replaceEmployee: false,
        phoneNumbers: [] as PhoneNumberDraft[],
        ...((company as Company)?.orgTreeNextLevelKeys?.reduce(
          (acc: any, curr, currentIndex) => {
            if (currentIndex === 0) {
              acc[curr.id] = company.id;
            } else {
              acc[curr.id] = '';
            }
            return acc;
          },
          {}
        ) || {}),
      };

  const {
    handleSubmit,
    handleChange,
    handleBlur,
    resetForm,
    values,
    errors,
    touched,
    setErrors,
    setFieldValue,
    setFieldTouched,
  } = useFormik({
    enableReinitialize: true,
    initialValues: initialValues,
    onSubmit: (values) => {
      if (employeeSelectedForEditing) {
        updateEmployee({
          variables: {
            id: employeeSelectedForEditing.id,
            input: {
              firstName: values.firstName,
              lastName: values.lastName,
              employeeId: values.employeeId,
              emailAddress: values.emailAddress,
              managementRoles: values.managementRoles.map(
                (role: ManagementRole) => ({
                  id: role.id,
                  name: role.name,
                })
              ),
              companyId: (company as Company).id,
              department: values.organization,
              // Not `hasAuthorizedReceivingSmsMessages: false`, which this
              // used to send: every edit, even a name, stopped the
              // employee's texts. Consent is the employee's to give.
              ...(multiplePhoneNumbers
                ? { phoneNumbers: draftsForSaving(values.phoneNumbers) }
                : {}),
            },
          },
        })
          .then(() => {
            resetForm();
            refetchEmployeeList();
            setOpen(false);
          })
          .catch((error: any) => {
            console.error('Error updating employee:', error);
            setErrors({ general: error.message });
          });
      } else {
        addEmployee({
          variables: {
            input: {
              firstName: values.firstName,
              lastName: values.lastName,
              employeeId: values.employeeId,
              emailAddress: values.emailAddress,
              managementRoles: values.managementRoles.map(
                (role: ManagementRole) => ({
                  id: role.id,
                  name: role.name,
                })
              ),
              companyId: (company as Company).id,
              department: values.organization,
              hasAuthorizedReceivingSmsMessages: false,
              ...(multiplePhoneNumbers
                ? { phoneNumbers: draftsForSaving(values.phoneNumbers) }
                : {}),
              restoreEmployee: values.restoreEmployee,
              replaceEmployee: values.replaceEmployee,
            },
          },
        })
          .then(() => {
            resetForm();
            refetchEmployeeList();
            setOpen(false);
          })
          .catch((error: any) => {
            console.error('Error adding employee:', error);
            if (
              error?.message.match(
                new RegExp(
                  `Employee with ID ${values.employeeId}, named .+ already exists but is archived`
                )
              )
            ) {
              setEmployeeExistsButIsArchivedErrorMessage(error.message);
            } else {
              setErrors({ general: error.message });
            }
          });
      }
    },
    validationSchema: Yup.object({
      firstName: Yup.string().required(t('Required')),
      lastName: Yup.string().required(t('Required')),
      employeeId: Yup.string().required(t('Required')),
      emailAddress: Yup.string()
        .email(t('Invalid email address'))
        .required(t('Required')),
    }),
  });

  if (isCompanyLoading) {
    return <LoadingSpinner spinnerId="add-employee-form-spinner" />;
  }

  const getPartsFromErrorMessage = (errorMessage: string) => {
    const employeeId =
      (typeof errorMessage === 'string' &&
        errorMessage.match(/ID (\w+)/)?.[1]) ||
      '';
    const name =
      (typeof errorMessage === 'string' &&
        errorMessage.match(/named (.+) already exists/)?.[1]) ||
      '';
    return { employeeId, name };
  };

  return (
    <div>
      {!employeeSelectedForEditingHasValidDepartment &&
        employeeSelectedForEditing && (
          <div className="mb-4 text-sm">
            <p className="text-semantic-error">
              {t('invalid-department', {
                nearestValidDepartment: nearestValidDepartmentList(
                  company as Company,
                  employeeSelectedForEditing?.department as string[]
                ),
              })}
            </p>
          </div>
        )}
      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-6 gap-3">
          <div className="col-span-3">
            <label
              htmlFor="firstName"
              className="block text-sm font-medium text-gray-700"
            >
              {t('First Name')}
            </label>
            <input
              type="text"
              name="firstName"
              id="firstName"
              autoComplete="given-name"
              className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.firstName}
            />
            {errors.firstName && touched.firstName && (
              <ErrorMessage
                errors={errors.firstName}
                touched={touched.firstName}
              />
            )}
          </div>
          <div className="col-span-3">
            <label
              htmlFor="lastName"
              className="block text-sm font-medium text-gray-700"
            >
              {t('Last Name')}
            </label>
            <input
              type="text"
              name="lastName"
              id="lastName"
              autoComplete="family-name"
              className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.lastName}
            />
            {errors.lastName && touched.lastName && (
              <ErrorMessage
                errors={errors.lastName}
                touched={touched.lastName}
              />
            )}
          </div>
          <div className="col-span-2">
            <label
              htmlFor="employeeId"
              className="block text-sm font-medium text-gray-700"
            >
              {t('Employee ID')}
            </label>
            <input
              type="text"
              name="employeeId"
              id="employeeId"
              autoComplete="off"
              className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.employeeId}
            />
            {errors.employeeId && touched.employeeId && (
              <ErrorMessage
                errors={errors.employeeId}
                touched={touched.employeeId}
              />
            )}
          </div>
          <div className="col-span-2">
            <label
              htmlFor="emailAddress"
              className="block text-sm font-medium text-gray-700"
            >
              {t('Email address')}
            </label>
            <input
              type="text"
              name="emailAddress"
              id="emailAddress"
              autoComplete="email"
              className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.emailAddress}
            />
            {errors.emailAddress && touched.emailAddress && (
              <ErrorMessage
                errors={errors.emailAddress}
                touched={touched.emailAddress}
              />
            )}
          </div>
          <MultiselectDropdown
            id="managementRoles"
            className="col-span-2"
            label={t('Roles')}
            values={values.managementRoles}
            options={(company as Company)?.managementRoles || []}
            onChange={(items) => {
              setFieldValue('managementRoles', items);
              setFieldTouched('managementRoles', true, false);
            }}
            mapPayload={(item) => ({
              id: item.id,
              label: item.name,
              payload: item,
            })}
            staticMenuItems={[
              {
                id: 'add-role',
                label: t('Add Role'),
                onClick: () => {
                  setAddManagementRoleModalOpen(true);
                },
              },
            ]}
            includeNoneOption
          />
          {multiplePhoneNumbers && (
            <div className="col-span-6">
              <PhoneNumberList
                legend={t('Phone numbers')}
                description={t(
                  "A number you add here gets no texts until the employee confirms it. We'll email them a link to do that."
                )}
                value={values.phoneNumbers ?? []}
                statusById={statusById}
                onChange={(list) => {
                  setFieldValue('phoneNumbers', list);
                  setFieldTouched('phoneNumbers', true, false);
                }}
              />
              {employeeSelectedForEditing &&
                savedNumbers.some((n) => n.status === 'NEEDS_CONFIRMING') && (
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <Button
                      type="button"
                      variant="secondary"
                      className="min-h-[44px] text-base"
                      onClick={async () => {
                        try {
                          const { data } = await sendConsentLink({
                            variables: { id: employeeSelectedForEditing.id },
                          });
                          setConsentLinkMessage(
                            data?.sendEmployeeConsentLink
                              ? t('We emailed {{email}} a link to confirm their numbers.', {
                                  email: values.emailAddress,
                                })
                              : t('A link was already sent in the last day.')
                          );
                        } catch (error: any) {
                          setConsentLinkMessage(error.message);
                        }
                      }}
                    >
                      {t('Send consent link')}
                    </Button>
                    <span role="status" className="text-base text-gray-600">
                      {consentLinkMessage}
                    </span>
                  </div>
                )}
            </div>
          )}
          <div className="col-span-6">
            <ObjectPillList
              list={values.organization ? [values.organization] : []}
              onClickXMark={(items: any[]) => setFieldValue('organization', [])}
              mapItem={(item) => {
                const parts = Array.isArray(item) ? item : [item];
                return {
                  key: parts.join('.'),
                  label:
                    departmentStringToFriendlyString(
                      company as Company,
                      parts.join('.')
                    ) || parts.join('.'),
                };
              }}
            />
            <OrgTreeSelector
              onSelectOrganization={(orgPath) => {
                setFieldValue('organization', orgPath.split('.'));
                setFieldTouched('organization', true, false);
              }}
              companyData={company as Company}
              refetchCompany={refetchCompany}
            />
          </div>
          <ErrorMessage errors={errors.general} touched />
        </div>
        {employeeExistsButIsArchivedErrorMessage ? (
          <div className="col-span-2">
            <ErrorMessage
              errors={t(
                'Employee with ID {{employeeId}}, named {{name}} already exists but is archived',
                getPartsFromErrorMessage(
                  employeeExistsButIsArchivedErrorMessage
                )
              )}
              touched={true}
            />
            <div className="mt-2">
              <Toggle
                id="restoreEmployee"
                label={t('Restore Employee')}
                enabled={values.restoreEmployee}
                setEnabled={(enabled: boolean) => {
                  setFieldValue('restoreEmployee', enabled);
                  if (enabled) {
                    setFieldValue('replaceEmployee', false);
                  }
                }}
              />
              <p className="text-sm text-gray-500 mt-2">
                {t(
                  'This will restore the existing employee without updating their details.'
                )}{' '}
                <strong>
                  {t('Once restored you will be able to update their record.')}
                </strong>
              </p>
              {values.restoreEmployee && (
                <strong className="text-md text-gray-500 mt-2">
                  {t(
                    'IMPORTANT: Please resubmit the form to restore the employee.'
                  )}
                </strong>
              )}
            </div>
            <div className="mt-2">
              <Toggle
                id="replaceEmployee"
                label={t('Replace Employee')}
                enabled={values.replaceEmployee}
                setEnabled={(enabled: boolean) => {
                  setErrors({});
                  setFieldValue('replaceEmployee', enabled);
                  if (enabled) {
                    setFieldValue('restoreEmployee', false);
                  }
                }}
              />
              <p className="text-sm text-gray-500 mt-2">
                {t(
                  'This will replace the existing employee with the new details.'
                )}{' '}
                <strong className="text-warning">
                  {t('This is a destructive action and cannot be undone.')}
                </strong>
              </p>
              {values.replaceEmployee && (
                <strong className="text-md text-gray-500 mt-2">
                  {t(
                    'IMPORTANT: Please resubmit the form to replace the employee.'
                  )}
                </strong>
              )}
            </div>
          </div>
        ) : null}
        <div className="flex justify-end mt-5 space-x-2">
          <Button
            type="button"
            variant="secondary"
            onClick={() => {
              resetForm();
              setOpen(false);
            }}
          >
            {t('Cancel')}
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={
              isEmpty(touched) ||
              !isEmpty(errors) ||
              (multiplePhoneNumbers && !phoneNumberListIsValid(values.phoneNumbers ?? [])) ||
              (!!employeeExistsButIsArchivedErrorMessage &&
                !values.restoreEmployee &&
                !values.replaceEmployee)
            }
          >
            {t('Save')}
          </Button>
        </div>
      </form>
      <AddManagementRoleModal
        open={addManagementRoleModalOpen}
        setOpen={setAddManagementRoleModalOpen}
        refetchCompany={refetchCompany}
        selectNewRole={(role: ManagementRole) => {
          setFieldValue('managementRoles', [...values.managementRoles, role]);
          setAddManagementRoleModalOpen(false);
        }}
      />
    </div>
  );
}

export default AddEmployeeForm;
